import {
  mapCareerPathResponseToCareerPath,
  ContentNugget,
  ContentNuggetBookmark,
  NUGGET_VARIANTS,
  blueprintTransform,
  toolTransform,
} from '@backrs_ui/common-ui';

import { hydrateEducationalInvestment } from '@backrs_ui/grow-ui/src/pages/EducationalInvestment/utils';

function toPreviewMedia(mediaUrl?: string) {
  return mediaUrl ? { media_url: mediaUrl } : undefined;
}

export function mapBookmarksToContentNugget(bookmarks: ContentNuggetBookmark[]): ContentNugget[] {
  const bookmarksAsNuggets: ContentNugget[] = [...bookmarks]
    .sort((a, b) => {
      const aCreatedAt = new Date(a?.created_at).getTime();
      const bCreatedAt = new Date(b?.created_at).getTime();

      return bCreatedAt - aCreatedAt;
    })
    .map((bookmark) => {
      switch (true) {
        case !!bookmark.content_nugget:
          return bookmark.content_nugget;

        case !!bookmark.content_college:
          return {
            id: bookmark.content_college.id,
            title: bookmark.content_college.school_name,
            description: bookmark.content_college.description,
            slug: bookmark.content_college.id.toString(),
            variant: 'institution',
            college_info: bookmark.content_college,
            is_browsable: false,
            content_nugget_theme: 'University',
          };

        case !!bookmark.content_external_profile:
          return {
            id: bookmark.content_external_profile.id,
            title: `Connect with and Review ${bookmark.content_external_profile.first_name} ${bookmark.content_external_profile.last_name}'s Profile`,
            description: bookmark.content_external_profile.description,
            slug: bookmark.content_external_profile.id.toString(),
            variant: 'profile-external',
            content_nugget_theme: 'Profile',
            is_browsable: false,
            external_profile_info: bookmark.content_external_profile,
          };

        case !!bookmark.content_internship:
          return {
            id: bookmark.content_internship.id,
            title: bookmark.content_internship.role_title,
            description: bookmark.content_internship.ai_description,
            slug: bookmark.content_internship.id.toString(),
            star_rating: 0,
            value_rating: 0,
            variant: 'internship',
            content_nugget_theme: 'Internship',
            is_browsable: false,
            internship_info: bookmark.content_internship,
          };

        case !!bookmark.content_scholarship:
          return {
            id: bookmark.content_scholarship.id,
            title: bookmark.content_scholarship.name,
            description: bookmark.content_scholarship.description,
            slug: bookmark.content_scholarship.donor,
            format: 'scholarships',
            star_rating: 0,
            value_rating: 0,
            variant: 'scholarships',
            scholarship_info: bookmark.content_scholarship,
            is_browsable: false,
          };
        case !!bookmark.content_profile:
          return {
            id: bookmark.content_profile.id,
            title: `Connect with and Review ${`${bookmark.content_profile.first_name ? bookmark.content_profile.first_name : ''} ${bookmark.content_profile.last_name ? `${bookmark.content_profile.last_name[0]}.` : ''}`}'s Profile`,
            description: bookmark.content_profile.bio || '',
            slug: bookmark.content_profile.email,
            format: 'profile',
            star_rating: 0,
            content_nugget_theme: 'Profile',
            value_rating: 0,
            variant: 'profile-internal',
            is_browsable: false,
            profile_info: bookmark.content_profile,
          };
        case !!bookmark.content_career_path:
          return {
            id: bookmark.content_career_path.id,
            title: bookmark.content_career_path.name,
            description: bookmark.content_career_path.description,
            slug: bookmark.content_career_path.id.toString(),
            star_rating: 0,
            value_rating: 0,
            variant: 'career-path',
            content_nugget_theme: 'Career Path',
            is_browsable: false,
            career_path_info: mapCareerPathResponseToCareerPath(bookmark.content_career_path),
          };
        case !!bookmark.content_career_track: {
          return {
            id: bookmark.content_career_track.id,
            title: bookmark.content_career_track.name,
            description: bookmark.content_career_track.description,
            slug: bookmark.content_career_track.id.toString(),
            star_rating: 0,
            value_rating: 0,
            variant: 'career-track',
            content_nugget_theme: 'Career Track',
            is_browsable: false,
            career_track_info: bookmark.content_career_track,
            preview_media: toPreviewMedia(bookmark.content_career_track.media_url),
          };
        }
        case !!bookmark.content_post:
          return {
            id: bookmark.content_post.id,
            title: bookmark.content_post?.author?.first_name
              ? `${bookmark.content_post.author.first_name}'s Thread`
              : '',
            description: bookmark.content_post?.body,
            slug: bookmark.content_post?.id?.toString(),
            star_rating: 0,
            value_rating: 0,
            variant: 'post',
            content_nugget_theme: 'Community',
            is_browsable: false,
            post_info: bookmark.content_post,
          };
        case !!bookmark.content_generated:
          return {
            id: bookmark.content_generated.id,
            title: bookmark.content_generated.title,
            description: '',
            content_nugget_theme: 'For You',
            variant: 'generated-content',
            is_browsable: false,
            generated_content_info: bookmark.content_generated,
          };
        case !!bookmark.content_track_assessment_result:
          return {
            id: bookmark.content_track_assessment_result.id,
            slug: bookmark.content_track_assessment_result.id.toString(),
            star_rating: 0,
            value_rating: 0,
            variant: 'track-assessment-result',
            is_browsable: false,
            track_assessment_result_info: bookmark.content_track_assessment_result,
          };
        case !!bookmark.content_track_assessment_unlock:
          return {
            id: bookmark.content_track_assessment_unlock.id,
            title: bookmark.content_track_assessment_unlock.category?.name ?? '',
            star_rating: 0,
            value_rating: 0,
            variant: 'track-assessment-unlock',
            is_browsable: false,
            track_assessment_unlock_info: bookmark.content_track_assessment_unlock,
          };
        case !!bookmark.content_educational_investment: {
          const hydratedEducationalInvestment = hydrateEducationalInvestment(
            bookmark.content_educational_investment,
          );

          return {
            id: hydratedEducationalInvestment.id,
            title: hydratedEducationalInvestment.name,
            description: hydratedEducationalInvestment.description,
            slug: hydratedEducationalInvestment.code,
            star_rating: 0,
            value_rating: 0,
            variant: 'educational-investment',
            is_browsable: false,
            educational_investment_info: hydratedEducationalInvestment,
          };
        }
        case !!bookmark.content_tool:
          return toolTransform(bookmark.content_tool);
        case !!bookmark.content_employer:
          return {
            id: bookmark.content_employer.id,
            title: bookmark.content_employer.name,
            description: bookmark.content_employer.the_scoop_about,
            slug: bookmark.content_employer.slug,
            star_rating: 0,
            value_rating: 0,
            variant: 'employer',
            is_browsable: false,
            employer_info: bookmark.content_employer,
          };
        case !!bookmark.content_competency:
          return {
            id: bookmark.content_competency.id,
            title: bookmark.content_competency.title,
            description: bookmark.content_competency.description,
            slug: bookmark.content_competency.slug,
            star_rating: 0,
            value_rating: 0,
            variant: 'competency',
            content_nugget_theme: 'Competency',
            is_browsable: false,
            preview_media: toPreviewMedia(bookmark.content_competency.media_url),
          };
        case !!bookmark.content_major:
          return {
            id: bookmark.content_major.id,
            title: bookmark.content_major.name,
            description: bookmark.content_major.description || '',
            slug: bookmark.content_major.id.toString(),
            star_rating: 0,
            value_rating: 0,
            variant: 'major',
            content_nugget_theme: 'Major',
            is_browsable: false,
            major_info: bookmark.content_major,
          };
        case !!bookmark.content_industry:
          return {
            id: bookmark.content_industry.id,
            title: bookmark.content_industry.name,
            description: bookmark.content_industry.description || '',
            slug: bookmark.content_industry.id.toString(),
            star_rating: 0,
            value_rating: 0,
            variant: 'industry',
            content_nugget_theme: 'Industry',
            is_browsable: false,
            industry_info: bookmark.content_industry,
          };
        case !!bookmark.content_coach:
          return {
            id: bookmark.content_coach.id,
            title: `${bookmark.content_coach.first_name} ${bookmark.content_coach.last_name}`,
            description: bookmark.content_coach.headline || '',
            slug: bookmark.content_coach.id.toString(),
            star_rating: 0,
            value_rating: 0,
            variant: 'coach',
            content_nugget_theme: 'Coach',
            is_browsable: false,
            coach_info: bookmark.content_coach,
          };
        case !!bookmark.content_deep_dive:
          return {
            id: bookmark.content_deep_dive.id,
            title: bookmark.content_deep_dive.name,
            description: bookmark.content_deep_dive.nugget_description || '',
            slug: bookmark.content_deep_dive.id.toString(),
            star_rating: 0,
            value_rating: 0,
            variant: 'deep-dive',
            content_nugget_theme: 'Deep Dive',
            is_browsable: false,
            deep_dive_info: bookmark.content_deep_dive,
          };
        case !!bookmark.content_blueprint:
          return blueprintTransform();
        default:
          return null;
      }
    })
    .filter((nugget) => nugget) as ContentNugget[];

  return bookmarksAsNuggets;
}

export function getBookmarkIdForNugget(
  bookmarks: ContentNuggetBookmark[] | undefined,
  nuggetId?: number,
  variant?: ContentNugget['variant'],
): number | undefined {
  if (!bookmarks || !Array.isArray(bookmarks) || !variant) {
    return undefined;
  }

  if (!nuggetId && variant !== NUGGET_VARIANTS.BLUEPRINT) {
    return undefined;
  }

  switch (variant) {
    // Blueprint doesn't match by specific ID — any bookmark with content_blueprint counts
    case NUGGET_VARIANTS.BLUEPRINT:
      return bookmarks.find((b) => !!b.content_blueprint)?.id;

    case NUGGET_VARIANTS.POST:
      return bookmarks.find((b) => b.content_post?.id === nuggetId)?.id;

    case NUGGET_VARIANTS.SCHOLARSHIPS:
      return bookmarks.find((b) => b.content_scholarship?.id === nuggetId)?.id;

    case NUGGET_VARIANTS.INTERNSHIP:
      return bookmarks.find((b) => b.content_internship?.id === nuggetId)?.id;

    case NUGGET_VARIANTS.PROFILE_INTERNAL:
      return bookmarks.find((b) => b.content_profile?.id === nuggetId)?.id;

    case NUGGET_VARIANTS.PROFILE_EXTERNAL:
      return bookmarks.find((b) => b.content_external_profile?.id === nuggetId)?.id;

    case NUGGET_VARIANTS.CAREER_PATH:
      return bookmarks.find((b) => b.content_career_path?.id === nuggetId)?.id;

    case NUGGET_VARIANTS.CAREER_TRACK:
      return bookmarks.find((b) => b.content_career_track?.id === nuggetId)?.id;

    case NUGGET_VARIANTS.INSTITUTION:
      if (bookmarks.find((b) => b.content_college?.id === nuggetId)) {
        return bookmarks.find((b) => b.content_college?.id === nuggetId)?.id;
      }

      return bookmarks.find((b) => b.content_nugget?.id === nuggetId)?.id;

    case NUGGET_VARIANTS.GENERATED_CONTENT:
      return bookmarks.find((b) => b.content_generated?.id === nuggetId)?.id;

    case NUGGET_VARIANTS.TRACK_ASSESSMENT_RESULT:
      return bookmarks.find((b) => b.content_track_assessment_result?.id === nuggetId)?.id;

    case NUGGET_VARIANTS.TRACK_ASSESSMENT_UNLOCK:
      return bookmarks.find((b) => b.content_track_assessment_unlock?.id === nuggetId)?.id;

    case NUGGET_VARIANTS.EDUCATIONAL_INVESTMENT:
      return bookmarks.find((b) => b.content_educational_investment?.id === nuggetId)?.id;

    case NUGGET_VARIANTS.TOOL:
      return bookmarks.find((b) => b.content_tool?.id === nuggetId)?.id;

    case NUGGET_VARIANTS.EMPLOYER:
      return bookmarks.find((b) => b.content_employer?.id === nuggetId)?.id;

    case NUGGET_VARIANTS.COMPETENCY:
      return bookmarks.find((b) => b.content_competency?.id === nuggetId)?.id;

    case NUGGET_VARIANTS.MAJOR:
      return bookmarks.find((b) => b.content_major?.id === nuggetId)?.id;

    case NUGGET_VARIANTS.INDUSTRY:
      return bookmarks.find((b) => b.content_industry?.id === nuggetId)?.id;

    case NUGGET_VARIANTS.COACH:
      return bookmarks.find((b) => b.content_coach?.id === nuggetId)?.id;

    case NUGGET_VARIANTS.DEEP_DIVE:
      return bookmarks.find((b) => b.content_deep_dive?.id === nuggetId)?.id;

    default: // for all other nuggets
      return bookmarks.find((b) => b.content_nugget?.id === nuggetId)?.id;
  }
}
